import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { GpuServer } from '../types/models/GpuServer';
import type { ApiResponseGpuServer } from '../types/api/ApiResponseGpuServer';
import { isTransient } from '../app/Datacenter/GpuServers/gpuServerStatus';
import { isAbortError, jsendErrorMessage, readJsendBody } from './gpuServerResponse';
import { GPU_SERVER_POLL_INTERVAL_MS } from './useGpuServers';

/** The show route's refusal for an unknown id or another organisation's server. */
const NOT_FOUND_MESSAGE = 'GPU server not found';

type LoadMode = 'initial' | 'refresh' | 'poll';

export interface UseGpuServerReturn {
  server: GpuServer | null;
  /** Only the first load and a manual `refetch` set it — poll ticks are silent. */
  loading: boolean;
  /** The first load's or a manual refetch's failure (other than not-found). */
  error: string | null;
  /** Unknown id, another organisation's server, or no valid id at all (`id === null`). */
  notFound: boolean;
  /** A failed silent poll tick: the last good `server` is kept. */
  pollError: string | null;
  refetch: () => Promise<void>;
  /** Replace the server with a fresher copy (e.g. the one a DELETE returned); polling follows it. */
  applyServer: (server: GpuServer) => void;
}

/**
 * `GET /datacenters/gpu-servers/{id}` — one GPU server (docs/api-types/gpu-servers.md).
 *
 * Pass `null` for a route param that is not a positive integer: the page then renders not-found
 * without calling the API (the backend route is `{id:\d+}`; anything else hits the router's `fail`).
 *
 * Polls silently every 30 s while the server is `starting`, `deleting` or `unpaid`. It fetches once
 * per id, then only on `refetch` or a poll tick — never because `apiFetch` changed identity on an
 * OIDC silent renew.
 */
export const useGpuServer = (id: number | null): UseGpuServerReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [server, setServer] = useState<GpuServer | null>(null);
  const [loading, setLoading] = useState<boolean>(id !== null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState<boolean>(id === null);
  const [pollError, setPollError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedIdRef = useRef<number | null>(null);

  const load = useCallback(
    async (mode: LoadMode) => {
      if (id === null) {
        return;
      }
      if (mode === 'poll' && abortRef.current !== null) {
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (mode !== 'poll') {
        setLoading(true);
        setError(null);
      }
      const fail = (message: string) => (mode === 'poll' ? setPollError(message) : setError(message));
      try {
        const response = await apiFetchRef.current(`/datacenters/gpu-servers/${id}`, { signal: controller.signal });
        const body = await readJsendBody<ApiResponseGpuServer>(response);
        if (controller.signal.aborted) {
          return;
        }
        if (body?.status === 'success' && body.data?.server) {
          setServer(body.data.server);
          setNotFound(false);
          setError(null);
          setPollError(null);
          return;
        }
        if (body?.status === 'error' && body.message === NOT_FOUND_MESSAGE) {
          setServer(null);
          setNotFound(true);
          return;
        }
        fail(jsendErrorMessage(body, 'Could not load this GPU server.'));
      } catch (err) {
        if (isAbortError(err, controller.signal)) {
          return;
        }
        fail(err instanceof Error ? err.message : 'An error occurred while loading this GPU server.');
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          if (mode !== 'poll') {
            setLoading(false);
          }
        }
      }
    },
    [id],
  );

  useEffect(() => {
    if (id === null) {
      abortRef.current?.abort();
      abortRef.current = null;
      fetchedIdRef.current = null;
      setServer(null);
      setNotFound(true);
      setLoading(false);
      return;
    }
    if (auth.isAuthenticated && fetchedIdRef.current !== id) {
      fetchedIdRef.current = id;
      setServer(null);
      setNotFound(false);
      void load('initial');
    }
  }, [id, auth.isAuthenticated, load]);

  const shouldPoll = server !== null && isTransient(server.status);
  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      void load('poll');
    }, GPU_SERVER_POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [shouldPoll, load]);

  // Forgetting the fetched id makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      fetchedIdRef.current = null;
    },
    [],
  );

  const refetch = useCallback(() => load('refresh'), [load]);
  const applyServer = useCallback((next: GpuServer) => {
    setServer(next);
    setNotFound(false);
  }, []);

  return { server, loading, error, notFound, pollError, refetch, applyServer };
};

export default useGpuServer;
